import { ApiResult, FieldType, ViewModelBase, isEmptyOrWhitespace, observable } from "@shoothill/core";
import { runInAction, action, computed } from "mobx";
import moment from "moment";

import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { formatDate, formatTime } from "Utils/Format";
import {
    EnvironmentFormModel,
    EnvironmentProjectDetailsDTO,
    EnvironmentRelatedResultResponseDTO,
    EnvironmentPhotosDTO,
    EnvironmentPhotosFile,
    EnvironmentDTO,
    UpsertEnvironmentAndRelatedRequestDTO,
    EnvironmentAndRelatedResponseDTO,
    SupplierDTO,
} from "./EnvironmentFormModel";
import { NonConformanceListViewModel } from "../NonConformanceListViewModel";

export class EnvironmentFormViewModel extends ViewModelBase<EnvironmentFormModel> {
    public server: ServerViewModel = new ServerViewModel();

    constructor(id: string | null, projectId: string | null, nonConformanceTypeId: string | null) {
        super(new EnvironmentFormModel());
        this.setDecorators(EnvironmentFormModel);

        this.model.id = id;
        this.model.projectId = projectId!;
        this.model.nonConformanceTypeId = nonConformanceTypeId!;

        isEmptyOrWhitespace(this.model.id) ? this.loadRelated() : this.loadWithRelated();
    }

    // #region Shared

    @observable
    public environmentProjectDetails: EnvironmentProjectDetailsDTO | null = null;

    @computed
    public get projectDisplayName() {
        return `(${!isEmptyOrWhitespace(this.environmentProjectDetails?.projectReference) ? this.environmentProjectDetails?.projectReference : "--"} - 
                 ${!isEmptyOrWhitespace(this.environmentProjectDetails?.projectName) ? this.environmentProjectDetails?.projectName : "--"})`;
    }

    public nonConformanceTypeDisplayName = (list: NonConformanceListViewModel): string => {
        const id = isEmptyOrWhitespace(this.model.id) ? this.model.nonConformanceTypeId : this.environment?.nonConformanceTypeId;

        return list.nonConformanceTypes.find((i: any) => i.id === id)?.displayName ?? "";
    };

    // #endregion Shared

    // #region Properties

    @action
    public setSupplier = (value: SupplierDTO | null): void => {
        this.setValue("supplierId", value?.id ?? EnvironmentFormModel.DEFAULT_SUPPLIER_ID);

        // Reset the user Id.
        this.setValue("userId", EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID);
    };

    @computed
    public get supplier(): SupplierDTO | null {
        return this.model.suppliers.find((u) => u.id === this.model.supplierId) ?? null;
    }

    @computed
    public get suppliers() {
        return this.model.suppliers;
    }

    @action
    public setUser = (value: any | null): void => {
        this.setValue("userId", value?.id ?? EnvironmentFormModel.DEFAULT_ISSUEDTO_USER_ID);
    };

    @computed
    public get user(): any | null {
        return this.model.users.find((u) => u.id === this.model.userId) ?? null;
    }

    @computed
    public get users() {
        return this.model.users;
    }

    @computed
    public get filteredUsers() {
        // Inducted users should be filtered by the currently selected supplier/company.

        if (this.model.supplierId === EnvironmentFormModel.DEFAULT_SUPPLIER_ID) {
            return this.model.users.filter((u) => u.groupType === "WORKER_USER");
        }

        return this.model.users.filter((u) => (u.groupType === "INDUCTION_USER" && u.supplierId === this.model.supplierId) || u.groupType === "WORKER_USER");
    }

    @action
    public setRectificationRequiredChange = (value: string) => {
        this.setValue("rectificationRequired", value === "true");
    };

    @action
    public setIsOpenChange = (value: string) => {
        this.setValue("isOpen", value === "true");
    };

    // #endregion Properties

    // #region ReadOnly Properties

    @observable
    public environment: any = null;

    @computed
    public get readOnlyFormDate(): string {
        if (this.environment?.formDate) {
            return moment(this.environment?.formDate).format("DD/MM/YYYY");
        }
        return "";
    }

    public get readOnlySupplierDisplayName(): string {
        return this.suppliers.find((i: any) => i.id === this.environment?.supplierId)?.displayName ?? "";
    }

    @computed
    public get readOnlyUserDisplayName(): string {
        return this.users.find((i: any) => i.id === this.environment?.issuedToInductionUserId || i.id === this.environment?.workerUserId)?.displayName ?? "";
    }

    @computed
    public get readOnlyLocationDisplayName(): string {
        return this.environment?.location ?? "";
    }

    @computed
    public get readOnlyDescriptionDisplayName(): string {
        return this.environment?.description ?? "";
    }

    @computed
    public get readOnlyRectificationRequired(): boolean {
        return this.environment?.rectificationRequired ?? false;
    }

    @computed
    public get readOnlyRectificationDate(): string {
        return this.environment?.rectificationDate ? formatDate(this.environment?.rectificationDate) : "";
    }

    @computed
    public get readOnlyIsOpen(): boolean {
        return this.environment?.isOpen ?? false;
    }

    @computed
    public get readOnlyRectificationDescription(): string {
        return this.environment?.rectificationDescription ?? "";
    }

    @computed
    public get readOnlyRectificationDetails(): string {
        return this.environment?.rectificationDetails ?? "";
    }

    @computed
    public get readOnlyCreatedDateAndTime(): string {
        return this.environment?.createdDate ? `${formatDate(this.environment?.createdDate)} @ ${formatTime(this.environment?.createdDate)}` : "";
    }

    // #endregion ReadOnly Properties

    @observable
    public environmentPhotos = observable<EnvironmentPhotosDTO>([]);

    @observable
    public snackMessage = "";

    @observable
    public snackType = "";

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @computed
    public get hasId(): boolean {
        return this.model.id !== null && this.model.id !== undefined && this.model.id !== "";
    }

    @computed
    public get getTodayDateFormatted(): string {
        return this.model.createdDate ? moment(this.model.createdDate).format("DD/MM/YYYY").toString() : moment().format("DD/MM/YYYY").toString();
    }

    @computed
    public get isFormDisabled(): boolean {
        return this.model.id !== null && this.model.id !== undefined && this.model.id !== "";
    }

    // Start Validation

    @computed
    private get validateUserId() {
        const errorMessage = this.model.validateUserId;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateLocation() {
        const errorMessage = this.model.validateLocation;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateDescription() {
        const errorMessage = this.model.validateDescription;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }
    //End Validation

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @action
    public setEnvironmentPhotosFile = (file: EnvironmentPhotosFile) => {
        this.model.environmentPhotos.push(file);
    };

    @action
    public setEnvironmentRectificationPhotosFile = (file: EnvironmentPhotosFile) => {
        this.model.environmentRectificationPhotos.push(file);
    };

    @action
    public deleteEnvironmentPhotosFile = async (index: number): Promise<void> => {
        this.model.environmentPhotos[index].isDeleted = true;
    };

    @action
    public deleteEnvironmentRectificationPhotosFile = async (index: number): Promise<void> => {
        this.model.environmentRectificationPhotos[index].isDeleted = true;
    };

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
        this.environmentProjectDetails = null;
    };

    @action
    public handleCancel(projectId: string | null): void {
        this.reset();
        this.history.push(AppUrls.Client.Project.ConstructionQuality.replace(":projectid", projectId ? projectId : this.model.projectId) + "#nonconformance");
    }

    public upsert = async (): Promise<void> => {
        // APM: Review Note.
        // Shouldn't need this. The server viewmodel has and automatically sets an IsBusy flag.
        this.setIsLoading(true);

        // Create the request body dto.
        const model: EnvironmentDTO = this.model.toDto();

        let environmentPhotos: EnvironmentPhotosDTO[] = [];
        let environmentRectificationPhotos: EnvironmentPhotosDTO[] = [];

        // APM: Review Note.
        // Does this make sense? It implies that to upload any photos, I cannot have an environment
        // identifier. So uploads can only happen on new issues.
        if (!this.hasId) {
            this.model.environmentPhotos.forEach((environmentPhoto) => {
                const environment: EnvironmentPhotosDTO = {
                    safetyId: environmentPhoto.safetyId,
                    fileName: environmentPhoto.fileName,
                    photoUrl: environmentPhoto.photoURL,
                    createdByUserId: null,
                    isDeleted: false,
                };
                environmentPhotos.push(environment);
            });
            this.model.environmentRectificationPhotos.forEach((environmentRectification) => {
                const environmentRectificationPhoto: EnvironmentPhotosDTO = {
                    safetyId: environmentRectification.safetyId,
                    fileName: environmentRectification.fileName,
                    photoUrl: environmentRectification.photoURL,
                    createdByUserId: null,
                    isDeleted: false,
                };
                environmentRectificationPhotos.push(environmentRectificationPhoto);
            });
        }

        const request: UpsertEnvironmentAndRelatedRequestDTO = {
            environment: model,
            environmentPhotos: environmentPhotos,
            environmentRectificationPhotos: environmentRectificationPhotos,
        };

        return await this.server
            .command<EnvironmentAndRelatedResponseDTO>(
                () => this.Post(AppUrls.Server.Projects.Construction.NonConformance.Environment.Upsert, request),
                (result: EnvironmentAndRelatedResponseDTO) => {
                    runInAction(() => {
                        if (result) {
                            this.handleCancel(result.environment.projectId);
                        }
                    });
                },
                this.isMyModelValid,
                "There was an error trying to send the environment form",
            )
            .finally(() => this.setIsLoading(false));
    };

    public upsertRectification = async (): Promise<void> => {
        // APM: Review Note.
        // Shouldn't need this. The server viewmodel has and automatically sets an IsBusy flag.
        this.setIsLoading(true);

        // Create the request body dto.
        const model: EnvironmentDTO = this.model.toDto();

        let environmentRectificationPhotos: EnvironmentPhotosDTO[] = [];

        if (this.hasId) {
            this.model.environmentRectificationPhotos.forEach((environmentRectification) => {
                const environmentRectificationPhoto: EnvironmentPhotosDTO = {
                    safetyId: environmentRectification.safetyId,
                    fileName: environmentRectification.fileName,
                    photoUrl: environmentRectification.photoURL,
                    createdByUserId: null,
                    isDeleted: false,
                };
                environmentRectificationPhotos.push(environmentRectificationPhoto);
            });
        }

        const request: UpsertEnvironmentAndRelatedRequestDTO = {
            environment: model,
            environmentPhotos: [],
            environmentRectificationPhotos: environmentRectificationPhotos,
        };

        return await this.server
            .command<EnvironmentAndRelatedResponseDTO>(
                () => this.Post(AppUrls.Server.Projects.Construction.NonConformance.Environment.Upsert, request),
                (result: EnvironmentAndRelatedResponseDTO) => {
                    runInAction(() => {
                        if (result) {
                            this.handleCancel(result.environment.projectId);
                        }
                    });
                },
                this.isMyModelValid,
                "There was an error trying to send the environment rectification",
            )
            .finally(() => this.setIsLoading(false));
    };

    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) === false) {
            isValid = false;
        }

        return isValid;
    };

    public loadRelated = async (): Promise<void> => {
        this.setIsLoading(true);

        return await this.server
            .query<EnvironmentRelatedResultResponseDTO>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.NonConformance.Environment.Related}\\${this.model.projectId}\\${this.model.nonConformanceTypeId}`),
                (result) => {
                    runInAction(() => {
                        this.model.suppliers.replace(result.suppliers);
                        this.model.inductionUsers.replace(result.issuedToInductionUsers);
                        this.model.workerUsers.replace(result.workerUsers);
                        this.environmentProjectDetails = result.environmentProjectDetails;
                    });
                },
            )
            .finally(() => this.setIsLoading(false));
    };

    public loadWithRelated = async (): Promise<void> => {
        this.setIsLoading(true);

        return await this.server
            .query<any>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.NonConformance.Environment.WithRelatedById}\\${this.model.id}`),
                (result) => {
                    runInAction(() => {
                        this.model.suppliers.replace(result.suppliers);
                        this.model.inductionUsers.replace(result.issuedToInductionUsers);
                        this.model.workerUsers.replace(result.workerUsers);
                        this.environmentProjectDetails = result.environmentProjectDetails;
                        this.environment = result.environment;
                        this.environment.isOpenInitial = result.environment.isOpen;
                        this.model.fromDto(result.environment);

                        result.environmentPhotos.map((el: any) => this.setEnvironmentPhotosFile(el));
                        result.environmentRectificationPhotos.map((el: any) => this.setEnvironmentRectificationPhotosFile(el));
                    });
                },
            )
            .finally(() => this.setIsLoading(false));
    };

    public fileUpload = async (data: any): Promise<ApiResult<any>> => {
        const formData = new FormData();
        formData.append("formFile", data.formFile);
        formData.append("fileName", data.fileName);
        const apiResult = await this.Post<any>(AppUrls.Server.File.UploadFile, formData);
        if (apiResult) {
            if (!apiResult.wasSuccessful) {
                console.log(apiResult.errors);
                runInAction(() => {
                    this.setSnackMessage("Error uploading file please try again.");
                    this.setSnackType(this.SNACKERROR);
                    this.setSnackbarState(true);
                });
            }
        }
        return apiResult;
    };

    public fileChange = async (event: any, isCompleted: boolean = false): Promise<void> => {
        if (event.target.files.length > 0) {
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = null;
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                let fileToDisplay: EnvironmentPhotosFile = {
                    safetyId: null,
                    fileName: data.fileName,
                    photoURL: apiResult.payload,
                    isDeleted: false,
                    createdByUserId: "",
                };
                if (isCompleted) {
                    this.setEnvironmentRectificationPhotosFile(fileToDisplay);
                } else {
                    this.setEnvironmentPhotosFile(fileToDisplay);
                }
            }
        }
    };

    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, fileUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    // #region Bolierplate

    public async isFieldValid(fieldName: keyof FieldType<EnvironmentFormModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (this.server.IsSubmitted) {
            switch (fieldName) {
                case "userId": {
                    const result = this.validateUserId;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "location": {
                    const result = this.validateLocation;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "description": {
                    const result = this.validateDescription;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
            }
        } else {
            errorMessage = "";
            isValid = true;
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Bolierplate
}
